import { redirect } from 'react-router';
import PathwayRoute from '@/routes/build/PathwayRoute';
import NodeRoute from '@/routes/NodeRoute';
import EditPathwayModal from '@/components/pathways/EditPathwayModal';
import { deletePathway, getPathway, getPathwayParamInfo, getPathwayParams } from '@/api/pathways';
import { getCase, getCaseNodesWithParams } from '@/api/cases';
import { pathwayActions } from '@/stores/pathwayStore';
import { caseActions } from '@/stores/caseStore';
import { renameNode } from '@/api/nodes';
import BuildRoute from './BuildRoute';
import SystemRoute from './SystemRoute';
import { deleteSystem, getSystem, getSystemParams } from '@/api/systems';
import { systemActions } from '@/stores/systemStore';
import EditSystemModal from '@/components/systems/EditSystemModal';
import { resourcesActions } from '@/stores/resourcesStore';
import { getProjectExportUnits, getProjects } from '@/api/projects';
import { getCustomModel, getCustomModels, getRegistry, getSubpathwayRegistry } from '@/api/registry';
import TitleEditForm from '@/components/nodes/TitleEditForm';
import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import PathwayControlPanel from '@/components/pathways/bulk/PathwayControlPanel';
import ControlPanelButton from '@/components/pathways/bulk/ControlPanelButton';

import LocationEditRoute from './LocationEditRoute';
import ChoiceModal from '@/components/registry/ChoiceModal';
import CreateModelModal from '@/components/registry/CreateModelModal';
import CreateEquationModelModal from '@/components/registry/CreateEquationModelModal';
import ImportModelModal from '@/components/registry/ImportModelModal';
import { pathwayParamsActions } from '@/stores/pathwayParamsStore';
import { exportUnitsActions } from '@/stores/exportUnitsStore';
import ImportAspenPlusModal from '@/components/registry/aspen/ImportAspenPlusModal';
import PathwaySettings from '@/components/pathways/bulk/PathwaySettings';
import MonteCarloSettings from '@/components/pathways/bulk/MonteCarloSettings';
import PathwayRevisions from '@/components/pathways/bulk/PathwayRevisions';
import { nodeActions } from '@/stores/nodeStore';
import featureConfig from '@/featureConfig';
import { registryActions } from '@/stores/registryStore';

const fetchPathwayAndCaseData = async (pathwayId, caseId) => {
  if (caseId) {
    const [{ data: pathway }, { data: caseData }] = await Promise.all([getPathway(pathwayId), getCase(caseId)]);
    return { pathway, caseData };
  } else {
    const { data: pathway } = await getPathway(pathwayId);
    const { data: caseData } = await getCase(pathway.case_id);
    return { pathway, caseData };
  }
};

const initPathwayRegistry = async (projectId, pathway, caseData) => {
  const caseId = pathway.parent_case_id ?? caseData.parent_case_id;
  const nodeId = pathway.parent_node_id ?? caseData.parent_node_id;
  const isInternalPathway = caseId && nodeId;
  const registry = isInternalPathway ? await getSubpathwayRegistry(caseId, nodeId) : await getRegistry(projectId);
  const context = isInternalPathway ? { caseId, nodeId } : {};

  registryActions.init(projectId, registry?.data?.node_types, context);
  registryActions.loadSystems();

  return registry?.data;
};

const buildRouter = {
  path: '/projects/:projectId/build',
  element: <BuildRoute />,
  handle: {
    title: _ => {
      return { title: 'Build' };
    },
  },
  children: [
    {
      path: '/projects/:projectId/build/pathways/:pathwayId/:caseId?',
      handle: {
        crumb: data => [
          'Pathways',
          data?.pathway?.name,
          data?.pathway?.locked ? <LockClosedIcon className="h-4 w-4" /> : <LockOpenIcon className="h-4 w-4" />,
        ],
        title: data => {
          return {
            title: `Build Pathway: ${data?.pathway?.name}`,
          };
        },
        controlPanel: () => <ControlPanelButton />,
      },
      id: 'pathway',
      element: <PathwayRoute />,
      errorElement: <PathwayRoute />,
      loader: async ({ params: { projectId, pathwayId, caseId } }) => {
        const { pathway, caseData } = await fetchPathwayAndCaseData(pathwayId, caseId);
        caseActions.init(caseData);
        const [, registry] = await Promise.all([
          pathwayActions.init(pathway, caseData),
          initPathwayRegistry(projectId, pathway, caseData),
        ]);
        return { pathway, caseData, registry };
      },
      children: [
        {
          path: '/projects/:projectId/build/pathways/:pathwayId/:caseId/edit',
          element: <EditPathwayModal title="Edit Pathway" />,
          loader: async ({ params: { pathwayId } }) => {
            const [
              { data: pathway },
              {
                data: { params },
              },
            ] = await Promise.all([getPathway(pathwayId), getPathwayParams(pathwayId)]);

            return { pathway, params };
          },
          action: EditPathwayModal.editAction,
        },
        {
          id: 'control-panel',
          loader: async ({ params: { projectId, pathwayId } }) => {
            const [
              {
                data: { custom_properties: customProperties = [] },
              },
              {
                data: { params: paramSchemas },
              },
              {
                data: { params },
              },
              {
                data: { settings: exportUnitSettings },
              },
            ] = await Promise.all([
              getPathway(pathwayId),
              getPathwayParamInfo(),
              getPathwayParams(pathwayId),
              getProjectExportUnits(projectId),
            ]);
            pathwayParamsActions.init(params);
            exportUnitsActions.init(exportUnitSettings);
            return { params, paramSchemas, customProperties };
          },
          path: '/projects/:projectId/build/pathways/:pathwayId/:caseId/control-panel',
          element: <PathwayControlPanel />,
          children: [
            {
              path: '/projects/:projectId/build/pathways/:pathwayId/:caseId/control-panel/settings',
              element: <PathwaySettings />,
            },
            {
              path: '/projects/:projectId/build/pathways/:pathwayId/:caseId/control-panel/monte-carlo',
              element: <MonteCarloSettings />,
              loader: ({ params: { pathwayId, caseId } }) => {
                return {
                  data: Promise.all([getPathway(pathwayId, { include_params: true }), getCaseNodesWithParams(caseId)]),
                };
              },
            },
            {
              path: '/projects/:projectId/build/pathways/:pathwayId/:caseId/control-panel/revisions',
              element: <PathwayRevisions />,
            },
          ],
        },
        {
          path: '/projects/:projectId/build/pathways/:pathwayId/:caseId/duplicate',
          element: <EditPathwayModal title="Duplicate Pathway" />,
          loader: async ({ params: { pathwayId } }) => {
            const [
              { data: pathway },
              {
                data: { next: projectsNextPage, results: projects },
              },
            ] = await Promise.all([getPathway(pathwayId), getProjects()]);

            return { pathway, projects, projectsNextPage };
          },
          action: EditPathwayModal.duplicateAction,
        },

        {
          path: '/projects/:projectId/build/pathways/:pathwayId/:caseId/choose-model',
          id: 'choose-model',
          element: <ChoiceModal />,
          loader: async () => {
            try {
              const [{ data: customModels }, { data: projects }] = await Promise.all([
                getCustomModels(),
                getProjects(),
              ]);
              return { customModels, projects };
            } catch {
              return { customModels: [], projects: [] };
            }
          },
          children: [
            {
              path: 'create-model',
              element: <CreateModelModal />,
            },
            {
              path: 'create-equation-model',
              element: <CreateEquationModelModal />,
            },
            {
              path: 'duplicate-equation-model/:customId',
              element: <CreateEquationModelModal mode="duplicate" />,
              loader: async ({ params: { customId } }) => {
                try {
                  const { data: customModel } = await getCustomModel(customId);
                  return { customModel };
                } catch {
                  return { customModel: null };
                }
              },
            },
            {
              path: 'import-from-aspen',
              element: <ImportAspenPlusModal />,
            },
            {
              path: 'import-model',
              element: <ImportModelModal />,
            },
          ],
        },

        {
          path: '/projects/:projectId/build/pathways/:pathwayId/:caseId/nodes/:nodeId',
          id: 'node',
          element: <NodeRoute />,
          loader: ({ params: { pathwayId, caseId, nodeId } }) => {
            const results = nodeActions.init(pathwayId, caseId, nodeId);

            return { results };
          },
          children: [
            {
              path: 'edit-title',
              element: <TitleEditForm />,
              action: async ({ request, params }) => {
                const form = await request.formData();
                const data = Object.fromEntries(form);
                const { caseId, nodeId } = params;

                await renameNode(caseId, nodeId, data.name);
                return redirect('..');
              },
            },
            {
              path: 'edit-location',
              element: <LocationEditRoute />,
            },
          ],
        },
        {
          path: '/projects/:projectId/build/pathways/:pathwayId/:caseId/destroy',
          action: async ({ params }) => {
            const { projectId, pathwayId } = params;
            const pathways = resourcesActions.getPathways();
            const filteredPathways = pathways.filter(pathway => pathway.id !== pathwayId);
            const currentPathwayId = filteredPathways?.[0]?.id;
            const currentCaseId = filteredPathways?.[0]?.case_id;

            const buildPathway = featureConfig.enablePathwayAdding
              ? `/projects/${projectId}/build`
              : `/projects/${projectId}`;
            const url = currentPathwayId
              ? `/projects/${projectId}/build/pathways/${currentPathwayId}/${currentCaseId}`
              : buildPathway;

            await deletePathway(pathwayId);

            return redirect(url);
          },
        },
      ],
    },
    {
      path: '/projects/:projectId/build/pathways/new',
      element: <EditPathwayModal title="New Pathway" />,
      action: EditPathwayModal.addAction,
    },
    {
      path: '/projects/:projectId/build/systems/:systemId',
      id: 'system',
      element: <SystemRoute />,
      errorElement: <SystemRoute />,
      handle: {
        crumb: data => ['Systems', data?.system?.name],
        title: data => {
          return { title: `Build System: ${data?.system?.name}` };
        },
      },
      loader: async ({ params: { systemId } }) => {
        const [
          { data: system },
          {
            data: { params },
          },
        ] = await Promise.all([getSystem(systemId), getSystemParams(systemId)]);

        await systemActions.init(system, params);
        return { system };
      },
      children: [
        {
          path: '/projects/:projectId/build/systems/:systemId/edit',
          element: <EditSystemModal action="edit" title="Rename System" />,
          loader: async ({ params: { systemId } }) => {
            const { data: system } = await getSystem(systemId);
            return { system };
          },
          action: EditSystemModal.editAction,
        },
        {
          path: '/projects/:projectId/build/systems/:systemId/duplicate',
          element: <EditSystemModal action="duplicate" title="Duplicate System" />,
          loader: async ({ params: { systemId } }) => {
            const [
              { data: system },
              {
                data: { next: projectsNextPage, results: projects },
              },
            ] = await Promise.all([getSystem(systemId), getProjects()]);

            return { projects, projectsNextPage, system };
          },
          action: EditSystemModal.duplicateAction,
        },
        {
          path: '/projects/:projectId/build/systems/:systemId/destroy',
          action: async ({ params }) => {
            const { projectId, systemId } = params;
            const systems = resourcesActions.getSystems();
            const filteredSystems = systems.filter(system => system.id !== systemId);
            const currentSystemId = filteredSystems?.[0]?.id;
            const url = currentSystemId
              ? `/projects/${projectId}/build/systems/${currentSystemId}`
              : `/projects/${projectId}/build`;

            await deleteSystem(systemId);
            return redirect(url);
          },
        },
      ],
    },
    {
      path: '/projects/:projectId/build/systems/new',
      element: <EditSystemModal action="add" title="New System" />,
      action: EditSystemModal.addAction,
    },
  ],
};

export default buildRouter;
